import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, ShieldCheck } from "lucide-react";
import { Logo } from "@/components/brand/Logo";
import { useAuth } from "@/context/AuthContext";
import { COMPANY } from "@/lib/company";
import { PhoneLink } from "@/components/app/PhoneLink";
import { TERMS, PRIVACY, LEGAL_UPDATED } from "@/lib/legalContent";
import { cn } from "@/lib/utils";

// Public legal pages (/terms, /privacy); members see them inside the app shell.
export default function Legal({ kind }) {
  const doc = kind === "privacy" ? PRIVACY : TERMS;
  const Icon = kind === "privacy" ? ShieldCheck : FileText;
  const { user } = useAuth();
  const navigate = useNavigate();
  return (
    <div className={cn("text-white", !user && "min-h-screen bg-am-bg")} data-testid={`legal-page-${kind}`}>
      {!user && (
        <header className="sticky top-0 z-40 flex items-center justify-between border-b border-am-border bg-am-bg/90 px-4 py-3 backdrop-blur-xl">
          <button onClick={() => navigate("/")} data-testid="legal-public-logo" className="flex items-center gap-2"><Logo size={30} /><span className="font-mark text-base font-extrabold tracking-tight">AURAMAX</span></button>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate("/login")} data-testid="legal-public-login" className="rounded-full px-3 py-1.5 text-sm font-semibold text-am-text2 hover:text-white">Log in</button>
            <button onClick={() => navigate("/join")} data-testid="legal-public-join" className="rounded-full aura-gradient px-4 py-1.5 text-sm font-bold text-black">Join AuraMax</button>
          </div>
        </header>
      )}
      <main className="mx-auto max-w-2xl px-4 py-5 pb-24 md:px-6">
        {user && <button onClick={() => navigate(-1)} data-testid="legal-back" className="mb-4 flex items-center gap-1 text-sm text-am-text2 hover:text-white"><ArrowLeft className="h-4 w-4" /> Back</button>}
        <article className="relative overflow-hidden rounded-3xl border border-am-border bg-am-surface p-6 sm:p-8 animate-[fadeIn_.4s_ease]">
          <span className="absolute inset-x-0 top-0 h-px aura-gradient opacity-80" />
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-am-text3"><Icon className="h-4 w-4" /> {COMPANY.legalName}</div>
          <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">{doc.title}</h1>
          <p className="mt-1 text-xs text-am-text3" data-testid="legal-updated">Last updated {LEGAL_UPDATED}</p>
          <p className="mt-5 text-sm leading-relaxed text-am-text2">{doc.intro}</p>
          <div className="mt-6 space-y-6">
            {doc.sections.map((s) => (
              <section key={s.h}>
                <h2 className="text-base font-bold text-white md:text-lg">{s.h}</h2>
                {s.p.map((t) => <p key={t} className="mt-2 text-sm leading-relaxed text-am-text2">{t}</p>)}
              </section>
            ))}
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-am-border pt-5 text-xs text-am-text3">
            <Link to={kind === "privacy" ? "/terms" : "/privacy"} data-testid="legal-switch" className="hover:text-white">{kind === "privacy" ? "Terms of Service" : "Privacy Policy"}</Link>
            <Link to="/contact" data-testid="legal-contact" className="hover:text-white">Contact</Link>
            <PhoneLink testid="legal-phone" className="text-xs" />
          </div>
        </article>
      </main>
    </div>
  );
}
